import { sortBy } from "lodash"

import { formatName } from "./namingUtils"

export type PreorderDisplayColor = "red" | "yellow" | "none"

export const getUnfulfillableDescriptionShort = () =>
  `Confirmed shortfall. Stock and incoming orders cannot fulfill these ${formatName("preorder", true)} within lead time.`

export const getUnfulfillableDescriptionLong = () =>
  `Current stock cannot fulfill this ${formatName("preorder")} and the order is within lead time, meaning no new stock can arrive before the delivery date.`

export const getAtRiskDescriptionShort = () =>
  `Projected shortfall. Stock may not cover these ${formatName("preorder", true)} at delivery, but new stock can still arrive or demand may differ from the forecast.`

export const getAtRiskDescriptionLong = () =>
  `These orders are at risk based on current demand forecasts. If sales follow the projected trend, stock will run out before the delivery date, setting these ${formatName("preorder", true)} at risk of not being fulfilled at delivery time.`

export const getUnfulfillableSyncDescription = () =>
  `Sets each SKU's quantity to its suggested order plus confirmed unfulfillable ${formatName("preorder")} units, which is demand stock can't cover before its delivery date and the suggested order excludes. Ordering these units lets them be fulfilled as backorders when this order arrives. Overwrites manual edits.`

export const getAtRiskSyncDescription = () =>
  `Same as "Confirmed shortfall only", plus projected at-risk units: ${formatName("preorder", true)} forecast to become unfulfillable if ${formatName("sale", true)} follow the current trend. Overwrites manual edits.`

type VisiblePreorder = { displayColor: PreorderDisplayColor; warehouseId: string; channel: string }

export function isPreorderVisible(
  po: VisiblePreorder | null | undefined,
  warehouseId?: string | null,
  channel?: string | null,
): po is VisiblePreorder {
  if (!po || po.displayColor === "none") return false
  if (warehouseId && po.warehouseId !== warehouseId) return false
  if (channel && po.channel !== channel) return false
  return true
}

export type PreorderTimingCategory = "within_leadtime" | "within_purchase_interval" | "outside"

const MS_PER_DAY = 24 * 60 * 60 * 1000

export function getTimingLabel(timing: PreorderTimingCategory): string {
  if (timing === "within_leadtime") return "Lead time"
  if (timing === "within_purchase_interval") return "Purchase period"
  return "Outside of purchase period"
}

/**
 * The two dates that split a pre-order's timeline: deliveries on/before `leadTimeEnd` can no
 * longer be re-ordered in time; deliveries after `purchasePeriodEnd` are outside the supply
 * window. Single source for both the timing classification and the "Why" dialog markers.
 */
export function getPreorderBoundaries(
  today: Date,
  totalLeadTimeDays: number,
  purchaseIntervalDays: number,
): { leadTimeEnd: Date; purchasePeriodEnd: Date } {
  return {
    leadTimeEnd: new Date(today.getTime() + totalLeadTimeDays * MS_PER_DAY),
    purchasePeriodEnd: new Date(
      today.getTime() + (totalLeadTimeDays + purchaseIntervalDays) * MS_PER_DAY,
    ),
  }
}

export function getTimingCategory(
  deliveryDate: Date,
  today: Date,
  totalLeadTimeDays: number,
  purchaseIntervalDays: number,
): PreorderTimingCategory {
  const { leadTimeEnd, purchasePeriodEnd } = getPreorderBoundaries(
    today,
    totalLeadTimeDays,
    purchaseIntervalDays,
  )
  if (deliveryDate.getTime() <= leadTimeEnd.getTime()) return "within_leadtime"
  if (deliveryDate.getTime() <= purchasePeriodEnd.getTime()) return "within_purchase_interval"
  return "outside"
}

type FuturePreorderInput = {
  skuId: string
  quantity: number
  deliveryDate: Date
  warehouseId: string
  channel: string
}

/** Forecasted sales demand for a SKU at a warehouse over [from, to). */
export type ForecastDemandFn = (skuId: string, warehouseId: string, from: Date, to: Date) => number

/**
 * Wraps a forecast getter to exclude known pre-orders, which the forecast already bakes in
 * at their delivery week — without this, the stock walk would double-count them (once as
 * forecast demand, once as an explicit pre-order event), inflating at-risk. Week-granular,
 * matching the forecast's own granularity.
 */
export function makeRegularDemandFn(
  getForecastDemand: ForecastDemandFn,
  knownPreorders: { skuId: string; warehouseId: string; deliveryDate: Date; quantity: number }[],
): ForecastDemandFn {
  const byKey = new Map<string, { date: number; quantity: number }[]>()
  for (const po of knownPreorders) {
    const key = `${po.skuId}|${po.warehouseId}`
    const arr = byKey.get(key) ?? []
    arr.push({ date: po.deliveryDate.getTime(), quantity: po.quantity })
    byKey.set(key, arr)
  }
  return (skuId, warehouseId, from, to) => {
    const raw = getForecastDemand(skuId, warehouseId, from, to)
    const fromMs = from.getTime()
    const toMs = to.getTime()
    let known = 0
    for (const po of byKey.get(`${skuId}|${warehouseId}`) ?? []) {
      if (po.date > fromMs && po.date <= toMs) known += po.quantity
    }
    return Math.max(0, raw - known)
  }
}

/**
 * Classifies each pre-order's shortfall as unfulfillable and/or at-risk via a single
 * date-precise stock walk per sku|warehouse. Two running balances advance together:
 * `committed` (current stock + incoming - known pre-orders) and `projected` (committed
 * minus forecasted demand). Incoming is credited before same-day pre-orders.
 *
 * unfulfillableQty = the committed shortfall, counted only within lead time (past that,
 * re-ordering can no longer arrive in time, so nothing is unfulfillable). atRiskQty = the
 * projected shortfall not already counted as unfulfillable — the forecast-driven excess
 * within lead time, and the whole projected shortfall outside it. With no `getForecastDemand`,
 * projected equals committed, so at-risk is just the committed shortfall outside lead time.
 *
 * Note: forecasted returns (which the server stockout model adds back) are not modelled
 * here, so at-risk is marginally conservative.
 */
export function addPreOrderFulfillment(
  knownPreorders: FuturePreorderInput[],
  skuStockLevels: { skuStockLevels: Record<string, number>; warehouseId: string }[],
  incomingOrders: {
    skuItems?: { skuId: string; quantity: number }[] | null
    expectedArrivalDate: Date
    warehouseId: string
  }[],
  today: Date,
  totalLeadTimeDays: number,
  purchaseIntervalDays: number,
  getForecastDemand?: ForecastDemandFn,
): (FuturePreorderInput & {
  unfulfillableQty: number
  atRiskQty: number
  displayColor: PreorderDisplayColor
})[] {
  if (knownPreorders.length === 0) return []

  const committed: Record<string, number> = {}
  const projected: Record<string, number> = {}
  for (const lvl of skuStockLevels) {
    for (const [skuId, qty] of Object.entries(lvl.skuStockLevels)) {
      const key = `${skuId}|${lvl.warehouseId}`
      committed[key] = (committed[key] ?? 0) + qty
      projected[key] = (projected[key] ?? 0) + qty
    }
  }

  // Last date through which forecast demand has been subtracted from projected, per key;
  // advances at each event so every interval is counted exactly once.
  const lastDemandDate: Record<string, number> = {}
  const shortfallByPo = new Map<FuturePreorderInput, { committed: number; projected: number }>()

  // Incoming and pre-orders walked as one flat timeline; `po` present ⇒ it's a pre-order.
  type WalkStep = {
    skuId: string
    warehouseId: string
    date: Date
    quantity: number
    po?: FuturePreorderInput
  }
  const steps: WalkStep[] = [
    ...knownPreorders.map((po) => ({
      skuId: po.skuId,
      warehouseId: po.warehouseId,
      date: po.deliveryDate,
      quantity: po.quantity,
      po,
    })),
    ...incomingOrders.flatMap((io) =>
      (io.skuItems ?? []).map(({ skuId, quantity }) => ({
        skuId,
        warehouseId: io.warehouseId,
        date: io.expectedArrivalDate,
        quantity,
      })),
    ),
  ]

  // A pre-order adds its quantity to the sort key (incoming adds 0), so on a shared date
  // incoming is credited before the pre-order.
  for (const step of sortBy(steps, (s) => s.date.getTime() + (s.po ? s.quantity : 0))) {
    const key = `${step.skuId}|${step.warehouseId}`

    if (getForecastDemand) {
      const from = new Date(lastDemandDate[key] ?? today.getTime())
      const demand = getForecastDemand(step.skuId, step.warehouseId, from, step.date)
      projected[key] = Math.max(0, (projected[key] ?? 0) - demand)
      lastDemandDate[key] = step.date.getTime()
    }

    if (!step.po) {
      committed[key] = (committed[key] ?? 0) + step.quantity
      projected[key] = (projected[key] ?? 0) + step.quantity
    } else {
      const availableCommitted = committed[key] ?? 0
      const availableProjected = projected[key] ?? 0
      shortfallByPo.set(step.po, {
        committed: Math.round(Math.max(0, step.quantity - availableCommitted)),
        projected: Math.round(Math.max(0, step.quantity - availableProjected)),
      })
      committed[key] = Math.max(0, availableCommitted - step.quantity)
      projected[key] = Math.max(0, availableProjected - step.quantity)
    }
  }

  return knownPreorders.map((po) => {
    if (po.quantity === 0) {
      return { ...po, unfulfillableQty: 0, atRiskQty: 0, displayColor: "none" as const }
    }

    const daysUntilDelivery = (po.deliveryDate.getTime() - today.getTime()) / MS_PER_DAY
    const isWithinLeadtime = daysUntilDelivery <= totalLeadTimeDays
    const outsideWindow = daysUntilDelivery > totalLeadTimeDays + purchaseIntervalDays

    const shortfall = shortfallByPo.get(po) ?? { committed: 0, projected: 0 }
    // Committed shortfall is unfulfillable only within lead time — past that it can still be
    // re-ordered, so it (plus any forecast-driven shortfall) counts as at-risk instead.
    const unfulfillableQty = isWithinLeadtime ? shortfall.committed : 0
    const atRiskQty = Math.max(0, shortfall.projected - unfulfillableQty)

    const displayColor: PreorderDisplayColor =
      outsideWindow || (atRiskQty <= 0 && unfulfillableQty <= 0)
        ? "none"
        : unfulfillableQty > 0
          ? "red"
          : "yellow"

    return { ...po, unfulfillableQty, atRiskQty, displayColor }
  })
}

export type EnrichedPreorder = ReturnType<typeof addPreOrderFulfillment>[number]

/**
 * Given enriched preorders (already have unfulfillableQty/atRiskQty from the store's stock walk)
 * and a cutoff date, returns per-SKU totals for preorders arriving before that date.
 */
export function computeUnfulfillableBySkuBeforeDate(
  preorders: { skuId: string; deliveryDate: Date; unfulfillableQty: number; atRiskQty: number }[],
  beforeDate: Date,
): Map<string, { unfulfillableQty: number; atRiskQty: number }> {
  const result = new Map<string, { unfulfillableQty: number; atRiskQty: number }>()
  for (const po of preorders) {
    if (po.deliveryDate >= beforeDate) continue
    const existing = result.get(po.skuId) ?? { unfulfillableQty: 0, atRiskQty: 0 }
    result.set(po.skuId, {
      unfulfillableQty: existing.unfulfillableQty + po.unfulfillableQty,
      atRiskQty: existing.atRiskQty + po.atRiskQty,
    })
  }
  return result
}

export type PreorderExplanation = {
  skuId: string
  size?: string | null
  warehouseId: string
  channel: string
  deliveryDate: Date
  quantity: number
  unfulfillableQty: number
  atRiskQty: number
  shortfall: number
  stockAtDelivery: number
  stockoutDate: Date | null
  timing: PreorderTimingCategory
  displayColor: PreorderDisplayColor
}
